import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plane,
  Receipt,
  MapPin,
  CalendarDays,
  Sparkles,
  Package,
  Book,
  Scale,
  Repeat,
  HeartPulse,
} from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";

const TABS = [
  { id: "trips", label: "Trips", Icon: Plane },
  { id: "money", label: "Money", Icon: Receipt },
  { id: "places", label: "Places", Icon: MapPin },
  { id: "timeline", label: "Timeline", Icon: CalendarDays },
  { id: "wishes", label: "Wishes", Icon: Sparkles },
  { id: "things", label: "Things", Icon: Package },
  { id: "decisions", label: "Decisions", Icon: Scale },
  { id: "routines", label: "Routines", Icon: Repeat },
  { id: "reflect", label: "Reflect", Icon: Book },
  { id: "health", label: "Health", Icon: HeartPulse },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function Library() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabId = (searchParams.get("tab") || "trips") as TabId;

  const setTab = useCallback(
    (next: TabId) => {
      const sp = new URLSearchParams(searchParams);
      if (next === "trips") sp.delete("tab");
      else sp.set("tab", next);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Library" }]} />

      <header className="space-y-1">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Library
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Your life as projected facts: trips, money, places, decisions,
          and the rest.
        </p>
      </header>

      <TeachingBanner
        id="library-intro"
        title="What is this?"
        body="Library is the long-term knowledge surface. Each tab projects
              from your claim ledger so counts are deduped. Trips, for
              instance, coalesce nearby flights and hotels into one trip
              instead of listing every confirmation email."
      />

      <nav aria-label="Library tabs" className="-mx-1 flex flex-wrap gap-1.5">
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = t.id === tabId;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition " +
                (active
                  ? "bg-emerald-500 text-white shadow ring-2 ring-emerald-300/40"
                  : "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 hover:bg-slate-200")
              }
              aria-pressed={active}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="pt-1">
        {tabId === "trips" && <TripsTab />}
        {tabId === "money" && <MoneyTab />}
        {tabId === "places" && <PlacesTab />}
        {tabId === "timeline" && <RedirectHint to="/timeline" label="Timeline" />}
        {tabId === "wishes" && <RedirectHint to="/wishes" label="Wishes" />}
        {tabId === "things" && <RedirectHint to="/things" label="Things" />}
        {tabId === "decisions" && <RedirectHint to="/decisions" label="Decisions" />}
        {tabId === "routines" && <RedirectHint to="/routines" label="Routines" />}
        {tabId === "reflect" && <RedirectHint to="/reflect" label="Reflect" />}
        {tabId === "health" && <RedirectHint to="/health" label="Health" />}
      </div>
    </div>
  );
}

interface Trip {
  id: string;
  title: string | null;
  destination: string | null;
  origin: string | null;
  start_at: string | null;
  end_at: string | null;
  segment_count: number | null;
  booking_count: number | null;
  provider: string | null;
  confirmed: number;
}

function TripsTab() {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true);
    fetch("/api/agenda/trips", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => setTrips(((j.trips as Trip[]) || []).slice(0, 60)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading...</div>;
  if (error)
    return (
      <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
        {error}
      </div>
    );
  if (trips.length === 0) {
    return (
      <Empty title="No trips found yet">
        Memso builds trips from flight, hotel, and rail confirmations in your
        mail. A reconnected Gmail or Outlook bridge usually surfaces the first
        trip within a few minutes.
      </Empty>
    );
  }
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {trips.map((t) => {
        const inferred = !t.confirmed;
        return (
          <li key={t.id}>
            <Link
              to={`/agenda/trips/${encodeURIComponent(t.id)}`}
              className={
                "block rounded-2xl p-4 transition hover:bg-white dark:hover:bg-slate-800 hover:shadow-md bg-white/70 dark:bg-slate-900/50 " +
                (inferred
                  ? "border-2 border-dashed border-amber-400/50 dark:border-amber-500/30"
                  : "border border-slate-200/70 dark:border-slate-700/40")
              }
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gradient-to-br from-sky-500 to-indigo-500 text-white">
                  <Plane className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {t.title || t.destination || "Trip"}
                  </p>
                  {(t.origin || t.destination) && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {t.origin || "?"} {"→"} {t.destination || "?"}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {formatRange(t.start_at, t.end_at)}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                <span>{t.segment_count ?? 0} leg{(t.segment_count ?? 0) === 1 ? "" : "s"} · {t.booking_count ?? 0} booking{(t.booking_count ?? 0) === 1 ? "" : "s"}</span>
                {inferred && (
                  <span className="rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-0.5 font-medium">
                    Inferred
                  </span>
                )}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

interface Transaction {
  id: string;
  merchant: string | null;
  amount: number | null;
  currency: string | null;
  captured_at: string | null;
  category: string | null;
}

function MoneyTab() {
  const [txns, setTxns] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true);
    fetch("/api/transactions?limit=80", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => setTxns(((j.transactions as Transaction[]) || (j.items as Transaction[]) || []).slice(0, 80)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading...</div>;
  if (error)
    return (
      <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
        {error}
      </div>
    );
  if (txns.length === 0) {
    return (
      <Empty title="No transactions yet">
        Money tiles populate from receipt emails (Stripe, Square, Amazon,
        rideshare, food delivery) and from the Simplifi connector once it is
        configured.
      </Empty>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Link
          to="/financial/subscriptions"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800/60 hover:bg-slate-200 text-sm font-semibold text-slate-700 dark:text-slate-200 px-3 py-1.5"
        >
          View subscriptions
        </Link>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {txns.map((t) => (
          <li
            key={t.id}
            className="rounded-xl border border-slate-200/70 dark:border-slate-700/40 bg-white/70 dark:bg-slate-900/50 p-3 space-y-1"
          >
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                {t.merchant || "Unknown merchant"}
              </p>
              {typeof t.amount === "number" && (
                <span className="text-sm font-bold text-slate-900 dark:text-slate-100 tabular-nums shrink-0">
                  {t.amount.toFixed(2)} {t.currency || ""}
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2">
              <span>{formatDate(t.captured_at)}</span>
              {t.category && <span className="rounded bg-slate-200 dark:bg-slate-700 px-1.5">{t.category}</span>}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface Place {
  id: string;
  display_name: string;
  visit_count: number | null;
  last_visited_at: string | null;
}

function PlacesTab() {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setLoading(true);
    fetch("/api/places?limit=60", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => setPlaces(((j.places as Place[]) || []).slice(0, 60)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading...</div>;
  if (error)
    return (
      <div className="rounded-lg border border-rose-300/60 bg-rose-50/60 dark:border-rose-700/40 dark:bg-rose-900/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
        {error}
      </div>
    );
  if (places.length === 0) {
    return (
      <Empty title="No places yet">
        Places accrete from trip destinations, calendar locations, and
        Facebook places-tagged history.
      </Empty>
    );
  }
  return (
    <ul className="flex flex-wrap gap-2">
      {places.map((p) => (
        <li key={p.id}>
          <Link
            to={`/places/${encodeURIComponent(p.id)}`}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-100 dark:bg-cyan-900/40 hover:bg-cyan-200 dark:hover:bg-cyan-800/60 text-cyan-900 dark:text-cyan-100 px-3 py-1.5 text-sm font-medium transition"
          >
            <MapPin className="w-3.5 h-3.5" />
            {p.display_name}
            {typeof p.visit_count === "number" && p.visit_count > 0 && (
              <span className="rounded-full bg-cyan-200 dark:bg-cyan-700 text-cyan-900 dark:text-cyan-100 px-1.5 text-[10px] font-bold">
                {p.visit_count}
              </span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function RedirectHint({ to, label }: { to: string; label: string }) {
  return (
    <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-5 space-y-2">
      <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Opening the {label.toLowerCase()} surface...
      </p>
      <Link
        to={to}
        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 text-sm font-semibold"
      >
        Open {label}
      </Link>
    </div>
  );
}

function Empty({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-6 text-center space-y-2">
      <p className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</p>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">{children}</p>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatRange(start: string | null, end: string | null): string {
  if (!start && !end) return "Dates pending";
  const s = formatDate(start);
  const e = formatDate(end);
  if (s && e) return `${s} - ${e}`;
  return s || e;
}
